import { useState, Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { useAuth } from "@/hooks/useAuth";
import { useSelectedOrganization } from "@/hooks/useSelectedOrganization";
import OrgSwitcher from "@/components/OrgSwitcher";
import { getAllSidebarSections, type NavItem } from "@/lib/dashboardNav";
import clsx from "clsx";

const SIDEBAR_COLLAPSED_KEY = "fhirfly-sidebar-collapsed";

function initials(name?: string, email?: string): string {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return (email?.[0] ?? "U").toUpperCase();
}

export default function CollapsibleSidebar() {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return stored === "true";
    }
    return false;
  });
  const [isHoveringDivider, setIsHoveringDivider] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { selectedOrganization } = useSelectedOrganization();

  // Save collapsed state to localStorage
  const toggleCollapsed = () => {
    const newValue = !isCollapsed;
    setIsCollapsed(newValue);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
  };

  const allSections = getAllSidebarSections(!!user?.is_superuser);

  const isActive = (href: string) => {
    if (href === "/dashboard") {
      return pathname === "/dashboard";
    }
    return pathname.startsWith(href);
  };

  const displayName = user?.name?.trim() || user?.email || "Account";
  const orgLabel = selectedOrganization?.name || "Personal account";

  return (
    <aside
      className={clsx(
        "relative flex flex-col bg-surface border-r border-neutral-subtle h-full transition-[width] duration-200 ease-out",
        isCollapsed ? "w-16" : "w-60"
      )}
    >
      {/* Brand mark */}
      <div className={clsx("pt-4 pb-2", isCollapsed ? "px-2" : "px-3")}>
        <Link
          to="/"
          aria-label="FHIRfly home"
          className="dash-brand"
        >
          {isCollapsed ? (
            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-brand-subtle text-brand-600 font-semibold text-sm">
              F
            </span>
          ) : (
            <img src="/logo-horiz-color-transparent.png" alt="FHIRfly" />
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        {allSections.map((section, sectionIndex) => (
          <div key={section.title} className={clsx(sectionIndex > 0 && "mt-3")}>
            {/* Section title - hidden when collapsed */}
            {!isCollapsed && (
              <div className="dash-grouplbl">{section.title}</div>
            )}

            <div className="space-y-0.5 px-2">
              {section.items.map((item) => (
                <NavItemComponent
                  key={item.href}
                  item={item}
                  isActive={isActive(item.href)}
                  isCollapsed={isCollapsed}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Account block - pinned to the bottom */}
      <Popover className="relative">
        {isCollapsed ? (
          <PopoverButton
            className="dash-acct justify-center"
            aria-label="Account menu"
          >
            <span className="avatar">{initials(user?.name, user?.email)}</span>
          </PopoverButton>
        ) : (
          <PopoverButton className="dash-acct" aria-label="Account menu">
            <span className="avatar">{initials(user?.name, user?.email)}</span>
            <span className="who">
              <b>{displayName}</b>
              <span>{orgLabel}</span>
            </span>
            <span className="caret" aria-hidden="true">
              <i className="fa-solid fa-chevron-up" />
            </span>
          </PopoverButton>
        )}

        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel
            className={clsx(
              "absolute z-50 w-64 bg-surface rounded-lg shadow-lg border border-neutral-subtle p-2",
              isCollapsed ? "left-full bottom-0 ml-2" : "left-2 right-2 bottom-full mb-2 w-auto"
            )}
          >
            <OrgSwitcher />
            <div className="my-1 border-t border-neutral-subtle" />
            <Link
              to="/dashboard/profile"
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md text-text-quiet hover:bg-surface-raised hover:text-text transition-colors"
            >
              <i className="fa-solid fa-user w-4 text-center" />
              Profile
            </Link>
            <button
              onClick={() => logout()}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-md text-text-quiet hover:bg-surface-raised hover:text-text transition-colors w-full text-left"
            >
              <i className="fa-solid fa-right-from-bracket w-4 text-center" />
              Sign out
            </button>
          </PopoverPanel>
        </Transition>
      </Popover>

      {/* Collapse/Expand Toggle */}
      <div
        className="absolute right-0 top-0 bottom-0 w-3 cursor-pointer flex items-center justify-center"
        onMouseEnter={() => setIsHoveringDivider(true)}
        onMouseLeave={() => setIsHoveringDivider(false)}
        onClick={toggleCollapsed}
      >
        {/* Chevron indicator - always visible */}
        <div className={clsx(
          "absolute right-0 translate-x-1/2 flex items-center justify-center w-4 h-6 rounded bg-surface border border-neutral-subtle shadow-sm transition-all",
          isHoveringDivider ? "bg-surface-raised border-neutral" : "bg-surface"
        )}>
          <i className={clsx(
            "fa-solid text-[9px] transition-colors",
            isHoveringDivider ? "text-text" : "text-text-quiet",
            isCollapsed ? "fa-chevron-right" : "fa-chevron-left"
          )} />
        </div>
      </div>
    </aside>
  );
}

interface NavItemComponentProps {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
}

function NavItemComponent({ item, isActive, isCollapsed }: NavItemComponentProps) {
  if (isCollapsed) {
    return (
      <div className="relative group">
        <Link
          to={item.href}
          className={clsx(
            "flex items-center justify-center w-full h-10 rounded-lg transition-colors",
            isActive
              ? "bg-brand-subtle text-brand-600"
              : "text-text-quiet hover:bg-surface-raised hover:text-text"
          )}
        >
          <i className={clsx(item.icon, "text-base")} />
        </Link>

        {/* Tooltip on hover */}
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 bg-neutral-900 text-white text-sm rounded-md shadow-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none">
          {item.label}
        </div>
      </div>
    );
  }

  return (
    <Link
      to={item.href}
      className={clsx(
        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
        isActive
          ? "bg-brand-subtle text-brand-600 font-medium"
          : "text-text-quiet hover:bg-surface-raised hover:text-text"
      )}
    >
      <i className={clsx(item.icon, "w-5 text-center")} />
      <span>{item.label}</span>
    </Link>
  );
}
